import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Type,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import {
  Automation,
  Choices,
  ChoicesInterface,
  RunningState,
  TaskDefinition,
  WorkflowStepInterface,
} from '@vendasta/automata';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { Category } from '../../../../../data-services/category';
import { DynamicComponentDirective } from '../../../../../common/dynamic-component.directive';
import { WORKFLOW_TRIGGERS } from '../../../../component-loader/trigger-definitions';
import { TaskDefinitionsService } from '../../../../../data-services/task-definitions.service';
import { CommonTriggerPanelComponent } from '../../common-trigger-panel.component';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { GalaxyInputModule } from '@vendasta/galaxy/input';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { GalaxyWrapModule } from '@vendasta/galaxy/galaxy-wrap';
import { GalaxyCheckboxModule } from '@vendasta/galaxy/checkbox';
import { TaskErrorDisclaimerComponent } from '../task-error-disclaimer/task-error-disclaimer.component';
import { TaskCategoryListComponent } from '../task-category-list/task-category-list.component';
import { AutomationsFeatureCache } from '@galaxy/automata/shared';

class TriggerPanelItem {
  constructor(
    public component: Type<CommonTriggerPanelComponent>,
    public taskDefinitionId: string,
    public automation: Automation,
    public triggerChoices: ChoicesInterface,
    public step: WorkflowStepInterface,
    public isWaitingForEvent: boolean,
    public waitForEventData: string,
  ) {}
}

export interface TriggerEditorControl {
  componentRef: ComponentRef<CommonTriggerPanelComponent>;
  taskDefinitionId: string;
  taskDefinition: TaskDefinition;
  triggerChoices: ChoicesInterface;
  originalTriggerChoices: ChoicesInterface;
  isEditing: boolean;
  savingEnabled$: Observable<boolean>;
}

@Component({
  selector: 'automata-trigger-editor-control',
  templateUrl: './trigger-editor-control.component.html',
  styleUrls: ['./trigger-editor-control.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyInputModule,
    GalaxyWrapModule,
    GalaxyCheckboxModule,
    TaskErrorDisclaimerComponent,
    TaskCategoryListComponent,
    DynamicComponentDirective,
  ],
})
export class TriggerEditorControlComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() automation: Automation;
  @Input() taskDefinitionId: string;
  @Input() step: WorkflowStepInterface;
  @Input() triggerChoices: ChoicesInterface;
  @Input() isWaitingForEvent: boolean;
  @Input() waitForEventData: string;

  @Input() set waitForDataType(value) {
    this.taskDefinitionsService.setWaitForDataType(value);
  }

  /** Emits an event once the component is created. */
  @Output() controlCreated: EventEmitter<any> = new EventEmitter();
  /** Emits an event once a new trigger is selected. */
  @Output() selectNewTrigger: EventEmitter<string> = new EventEmitter();
  /** Emits an event when the trigger is cleared, allowing going backwards in the workflow */
  @Output() triggerCleared: EventEmitter<any> = new EventEmitter();

  RUNNING: RunningState = RunningState.RUNNING_STATE_RUNNING;

  availableTriggers$: Observable<Category[]>;
  internalTriggers$: Observable<Category[]> = this.taskDefinitionsService.internalTriggersWithSearch$;

  showDisabledItems: boolean;

  searchControl: UntypedFormControl = new UntypedFormControl();

  destroyed$$ = new Subject<void>();

  /** Emits false when the editor or one of its subviews is still loading, true otherwise. */
  private savingEnabled$$ = new BehaviorSubject<boolean>(false);
  public savingEnabled$: Observable<boolean>;

  @ViewChild(DynamicComponentDirective, { static: true })
  dynamicComponent: DynamicComponentDirective;
  public componentRef: ComponentRef<CommonTriggerPanelComponent>;

  public taskDefinition: TaskDefinition;
  public originalTriggerChoices: ChoicesInterface;

  public isEditing = false;

  showInternalTriggers$: Observable<boolean> = this.automationsFeatureFlags.includeInternalFeatures$;

  ngAfterViewInit(): void {
    this.controlCreated.emit(this);
  }

  constructor(
    private readonly automationsFeatureFlags: AutomationsFeatureCache,
    private readonly taskDefinitionsService: TaskDefinitionsService,
    private readonly openConfirmationModalService: OpenConfirmationModalService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.savingEnabled$ = this.savingEnabled$$.asObservable().pipe(map(() => this.savingEnabled$$.value));

    this.originalTriggerChoices = this.triggerChoices;
    this.availableTriggers$ = this.taskDefinitionsService.getTriggers(this.isWaitingForEvent);

    this.taskDefinitionsService.setSearchTerm('');
    this.searchControl.valueChanges.pipe(takeUntil(this.destroyed$$)).subscribe((searchTerm) => {
      this.taskDefinitionsService.setSearchTerm(searchTerm);
    });

    if (this.taskDefinitionId) {
      this.enterEditTriggerView();
    } else {
      this.clearTriggerChoices();
    }
  }

  ngOnDestroy(): void {
    this.taskDefinitionsService.setWaitForDataType(undefined);
    this.destroyed$$.next();
    this.destroyed$$.complete();
  }

  triggerSelected(taskDefinition: TaskDefinition): void {
    this.taskDefinition = taskDefinition;
    this.taskDefinitionId = taskDefinition.id;
    const changingTriggerType =
      !!this.automation.triggerStep && this.automation.triggerStep.taskDefinitionId !== taskDefinition.id;

    // When not changing the existing trigger, or when inside the Delay Until Event step, we do not show the dialog
    // warning them about clearing their filters
    if (this.isWaitingForEvent || !changingTriggerType) {
      this.clearTriggerChoices();
      this.selectNewTrigger.emit(this.taskDefinitionId);
      this.enterEditTriggerView();
      return;
    }

    this.openConfirmationModalService
      .openModal({
        type: 'confirm',
        title: 'AUTOMATIONS.EDITOR.CLEAR_FILTERS.HEADER',
        message: 'AUTOMATIONS.EDITOR.CLEAR_FILTERS.BODY',
        confirmButtonText: 'AUTOMATIONS.EDITOR.CLEAR_FILTERS.CONTINUE',
      })
      .pipe(take(1))
      .subscribe((proceed) => {
        if (proceed) {
          this.clearTriggerChoices();
          this.selectNewTrigger.emit(this.taskDefinitionId);
          this.enterEditTriggerView();
        }
      });
  }

  enterEditTriggerView(): void {
    this.isEditing = true;

    const item = new TriggerPanelItem(
      WORKFLOW_TRIGGERS[this.taskDefinitionId].panel,
      this.taskDefinitionId,
      this.automation,
      this.triggerChoices,
      this.step,
      this.isWaitingForEvent,
      this.waitForEventData,
    );

    this.loadComponent(item); // displays the trigger selected
  }

  enterListOfTriggersView(): void {
    this.isEditing = false;
    this.taskDefinitionId = '';
    this.dynamicComponent.viewContainerRef.clear(); // removes trigger component
    this.triggerCleared.emit();
  }

  loadComponent(item: TriggerPanelItem): void {
    this.savingEnabled$$.next(false);
    // Stop listening to the old subcomponent and load a new one.
    if (this.componentRef) {
      this.componentRef.instance.savingEnabled$.unsubscribe();
    }
    const viewContainerRef = this.dynamicComponent.viewContainerRef;
    viewContainerRef.clear();
    this.componentRef = viewContainerRef.createComponent<CommonTriggerPanelComponent>(item.component);

    // Set up the new subcomponent.
    this.componentRef.instance.taskDefinitionId = item.taskDefinitionId;
    this.componentRef.instance.automation = item.automation;
    this.componentRef.instance.triggerChoices = item.triggerChoices;
    this.componentRef.instance.step = item.step;
    this.componentRef.instance.isWaitingForEvent = item.isWaitingForEvent;
    this.componentRef.instance.waitForEventData = item.waitForEventData;

    // Subscribe to its loading status.
    this.componentRef.instance.savingEnabled$
      .pipe(takeUntil(this.destroyed$$))
      .subscribe((savingEnabled) => this.savingEnabled$$.next(savingEnabled));

    // Force the component to re-render.
    this.componentRef.changeDetectorRef.detectChanges();

    // Simple panels (specifically those without async components) don't always cause this control to refresh,
    // so we need to manually do trigger the refresh just to be safe.
    this.cdr.detectChanges();
  }

  clearTriggerChoices(): void {
    this.triggerChoices = new Choices({
      rules: [],
    });
  }
}
